import { create } from "zustand";
import {
  createEmptyGaiaStore,
  GaiaStoreInterface,
} from "@/engine/viewer/core/useGaiaStore";
import { StoreApi, UseBoundStore } from "zustand";
import * as THREE from "three";
import { LayerDto } from "@/types/data-types";
import { SharedViewerStore } from "@/engine/viewer/core/useViewerStore";

interface GaiaStoreContainerInterface {
  /** Global cached data */
  hdri: THREE.DataTexture;
  /* Henge */
  viewingHengeObject: THREE.Object3D | null;
  viewingHengeLayers: LayerDto[] | null;
  layerInteractive: boolean;
  selectedLayerPath: string | null;
  selectedLayerObject: THREE.Object3D | null;
  /** current GaiaStore */
  currentGaiaStore: UseBoundStore<StoreApi<GaiaStoreInterface>>;
  /** Actions */
  gaiaStoreContainerActions: {
    initHdri: (hdri: THREE.DataTexture) => void;
    changeViewingHengeObject: (
      viewingHengeObject: THREE.Object3D | null,
    ) => void;
    changeViewingHengeLayers: (
      updateFn:
        | LayerDto[]
        | null
        | ((viewingHengeLayers: LayerDto[] | null) => LayerDto[] | null),
    ) => void;
    changeLayerInteractive: (layerInteractive: boolean) => void;
    changeSelectedLayer: (layerObject: THREE.Object3D | null) => void;
    changeViewingGaiaStore: (
      store: UseBoundStore<StoreApi<GaiaStoreInterface>>,
    ) => void;
  };
}

const EmptyGaiaStore = createEmptyGaiaStore(SharedViewerStore);

const useGaiaStoreContainer = create<GaiaStoreContainerInterface>(
  (set, get) => ({
    hdri: null as unknown as THREE.DataTexture,
    viewingHengeObject: null,
    viewingHengeLayers: null,
    layerInteractive: false,
    selectedLayerPath: null,
    selectedLayerObject: null,
    currentGaiaStore: EmptyGaiaStore,
    gaiaStoreContainerActions: {
      initHdri: (hdri: THREE.DataTexture) =>
        set(() => ({
          hdri: hdri,
        })),
      changeViewingHengeObject: (viewingHengeObject) =>
        set(() => ({
          viewingHengeObject,
        })),
      changeViewingHengeLayers: (updateFn) => {
        set((state) => ({
          viewingHengeLayers:
            typeof updateFn === "function"
              ? updateFn(state.viewingHengeLayers)
              : updateFn,
        }));
      },
      changeLayerInteractive: (layerInteractive) => {
        set({ layerInteractive });
      },
      changeSelectedLayer: (layerObject) => {
        const prevLayerPath = get().selectedLayerPath;
        const nextLayerPath: string | undefined =
          layerObject?.userData?.layer?.path;

        if (layerObject && nextLayerPath) {
          if (prevLayerPath && prevLayerPath === nextLayerPath) {
            set({
              selectedLayerPath: null,
              selectedLayerObject: null,
            });
          } else {
            set({
              selectedLayerPath: nextLayerPath,
              selectedLayerObject: layerObject,
            });
          }
        } else {
          set({
            selectedLayerPath: null,
            selectedLayerObject: null,
          });
        }
      },
      changeViewingGaiaStore: (currentGaiaStore) =>
        set(() => ({
          currentGaiaStore: currentGaiaStore,
        })),
    },
  }),
);

export default useGaiaStoreContainer;
export const useCurrentGaiaStore = () =>
  useGaiaStoreContainer((state) => state.currentGaiaStore);
export const useGaiaStoreContainerActions = () =>
  useGaiaStoreContainer((state) => state.gaiaStoreContainerActions);
